import { InForceProductGroup } from "@/enums/InForceProductGroup";
import { ClientInForceRequestData } from "@/models/ClientInForceRequestData";
import { InForceCaseData } from "@/models/InForceCaseData";
import { InForceCasesService } from "@/services/InForceCasesService";
import store from "@/store";
import _ from "lodash";
import { Action, Module, VuexModule } from "vuex-class-modules";
import { globalStore } from "./GlobalStore";

@Module({ generateMutationSetters: true })
class InForceStore extends VuexModule {
  service: InForceCasesService = new InForceCasesService();
  entities: InForceCaseData[] = [];
  current: InForceCaseData = new InForceCaseData();

  get totalCount(): number {
    return this.entities.length;
  }

  get totalNotifications(): number {
    return _.sum(this.entities.map((d) => d.notificationCount));
  }

  get totalPremium(): number {
    return this.entities
      .filter((p) => p.productGroup === InForceProductGroup.LIFE)
      .reduce((accumulator: number, item: InForceCaseData) => {
        return accumulator + (item.annualizedPremiumAtIssue ?? 0);
      }, 0);
  }

  get totalFaceAmount(): number {
    return this.entities
      .filter((p) => p.productGroup === InForceProductGroup.LIFE)
      .reduce((accumulator: number, item: InForceCaseData) => {
        return accumulator + (item.faceAmount ?? 0);
      }, 0);
  }
  get totalCashValue(): number {
    return this.entities
      .filter((p) => p.productGroup === InForceProductGroup.LIFE)
      .reduce((accumulator: number, item: InForceCaseData) => {
        return accumulator + (item.cashValue ?? 0);
      }, 0);
  }
  get totalContractValue(): number {
    return this.entities.reduce(
      (accumulator: number, item: InForceCaseData) => {
        return accumulator + (item.contractValue ?? 0);
      },
      0
    );
  }
  get totalDeathClaims(): number {
    return this.entities.filter(
      (p) =>
        p.deathClaimLastMonth === true &&
        p.productGroup === InForceProductGroup.LIFE
    ).length;
  }

  get totalLapsed(): number {
    return this.entities.filter(
      (p) =>
        p.laspsedLastMonth === true &&
        p.productGroup === InForceProductGroup.LIFE
    ).length;
  }

  get totalRiskOfLapsing(): number {
    return this.entities.filter(
      (p) =>
        p.atRiskOfLapsing === true &&
        p.productGroup === InForceProductGroup.LIFE
    ).length;
  }

  get totalTermExpired(): number {
    return this.entities.filter(
      (p) =>
        p.termLevelPeriodExpired === true &&
        p.productGroup === InForceProductGroup.LIFE
    ).length;
  }

  get totalTermExpiring(): number {
    return this.entities.filter(
      (p) =>
        p.termEndingIn180Days === true &&
        p.productGroup === InForceProductGroup.LIFE
    ).length;
  }

  get totalConversionExpiring(): number {
    return this.entities.filter(
      (p) =>
        p.conversionEndingIn180Days === true &&
        p.productGroup === InForceProductGroup.LIFE
    ).length;
  }

  get totalNearingRetirement(): number {
    return this.entities.filter(
      (p) =>
        p.nearingRetirement === true &&
        p.productGroup === InForceProductGroup.ANNUITY
    ).length;
  }

  get totalMarketValueAdjustment(): number {
    return this.entities.filter(
      (p) =>
        p.marketValueAdjustment === true &&
        p.productGroup === InForceProductGroup.ANNUITY
    ).length;
  }

  get totalSurrenderExpiring(): number {
    return this.entities.filter(
      (p) =>
        p.surrenderExpiring === true &&
        p.productGroup === InForceProductGroup.ANNUITY
    ).length;
  }

  get totalSurrenderExpired(): number {
    return this.entities.filter(
      (p) =>
        p.surrenderExpired === true &&
        p.productGroup === InForceProductGroup.ANNUITY
    ).length;
  }

  get totalRequiredMinimumDistribution(): number {
    return this.entities.filter(
      (p) =>
        p.requiredMinimumDistribution === true &&
        p.productGroup === InForceProductGroup.ANNUITY
    ).length;
  }
  get totalContractAmount(): number {
    return this.entities
      .filter((p) => p.productGroup === InForceProductGroup.ANNUITY)
      .reduce((accumulator: number, item: InForceCaseData) => {
        return accumulator + (item.contractValue ?? 0);
      }, 0);
  }
  get totalSurrenderAmount(): number {
    return this.entities
      .filter((p) => p.productGroup === InForceProductGroup.ANNUITY)
      .reduce((accumulator: number, item: InForceCaseData) => {
        return accumulator + (item.totalSurrenderValue ?? 0);
      }, 0);
  }
  get totalLifePolicies(): number {
    return _.size(
      this.entities.filter((d) => d.productGroup === InForceProductGroup.LIFE)
    );
  }
  get totalAnnuityPolicies(): number {
    return _.size(
      this.entities.filter(
        (d) => d.productGroup === InForceProductGroup.ANNUITY
      )
    );
  }
  get totalTrackedPolicies(): number {
    return this.entities.length;
  }
  @Action
  async getAll(): Promise<void> {
    try {
      globalStore.setIsBusy(true);
      const items = await this.service.getAll();
      this.entities = items.map((x) => _.merge(new InForceCaseData(), x));
    } finally {
      globalStore.setIsBusy(false);
    }
  }

  @Action
  async select(id: string): Promise<void> {
    try {
      globalStore.setIsBusy(true);
      const entity = new InForceCaseData();
      const data = await this.service.get(id);
      _.merge(entity, data);
      this.current = entity;
    } finally {
      globalStore.setIsBusy(false);
    }
  }

  @Action
  async inforceCases(request: ClientInForceRequestData): Promise<void> {
    try {
      globalStore.setIsBusy(true);
      const items = await this.service.getClientInForce(request);
      this.entities = items.map((x) => _.merge(new InForceCaseData(), x));
    } finally {
      globalStore.setIsBusy(false);
    }
  }
}

export const inForceStore = new InForceStore({ store, name: "in-force" });
